import { FilterCollection } from '$global/features/filter-drawer/constants';
import type {
	FilterCheckboxList,
	FilterDrawerCustomAttributes,
	FilterSection,
	FilterSectionFilterType,
	FilterSections,
	FilterToggle,
	GetCustomFilterAttribute,
} from '$global/features/filter-drawer/types';

// Structural mirror of the REST endpoint's `filterOptions` (see
// getTalksGalleryPage in $lib/websites/talks/api) — this module no longer
// depends on the retired getTalksFilterOptions GraphQL document.
export interface TalksGalleryFilterOptions {
	speakers: { name: string }[];
	series: { title: string | null }[];
	themes: { name: string | null }[];
	timePeriods: { name: string | null }[];
}

enum FilterSectionId {
	Speakers = '1',
	Series = '2',
	Themes = '3',
	TimePeriod = '4',
	LimitedTime = '5',
}

enum FilterTypeId {
	Speakers = '1',
	Series = '2',
	Themes = '3',
	TimePeriod = '4',
	LimitedTime = '5',
}
const getSpeakers = (
	speakers: TalksGalleryFilterOptions['speakers'] = []
): FilterSection => {
	const item: FilterCheckboxList = {
		__typename: 'Filter_Checkbox_List',
		id: 'speakers',
		field_name: 'speakers.HENI_Talks_Speakers_id.name',
		label: '',
		select_all: true,
		type: 'checkbox',
		filter_checkboxes: speakers?.map((speaker, index) => {
			return {
				__typename: 'Filter_Checkbox',
				id: index.toString(),
				label: speaker.name,
				default_state: false,
				value: speaker.name,
			};
		}),
	};

	const checkboxes: FilterSectionFilterType = {
		__typename: 'Filter_Section_filter_type',
		collection: FilterCollection.Filter_Checkbox_List,
		id: FilterTypeId.Speakers,
		item,
	};

	return {
		__typename: 'Filter_Section',
		id: FilterSectionId.Speakers,
		title: 'Speakers',
		default_open: true,
		show_content_only: false,
		filter_type: [checkboxes],
	};
};

const getSeries = (
	series: TalksGalleryFilterOptions['series'] = []
): FilterSection => {
	const item: FilterCheckboxList = {
		__typename: 'Filter_Checkbox_List',
		id: 'series',
		field_name: 'series.HENI_Talks_Series_id.title',
		label: '',
		select_all: true,
		type: 'checkbox',
		filter_checkboxes: series.map((speaker, index) => {
			return {
				__typename: 'Filter_Checkbox',
				id: index.toString(),
				label: speaker.title,

				default_state: false,
				value: speaker.title,
			};
		}),
	};

	const checkboxes: FilterSectionFilterType = {
		__typename: 'Filter_Section_filter_type',
		collection: FilterCollection.Filter_Checkbox_List,
		id: FilterTypeId.Series,
		item,
	};

	return {
		__typename: 'Filter_Section',
		id: FilterSectionId.Series,
		title: 'Series',
		default_open: true,
		show_content_only: false,
		filter_type: [checkboxes],
	};
};

const getThemes = (
	themes: TalksGalleryFilterOptions['themes'] = []
): FilterSection => {
	const item: FilterCheckboxList = {
		__typename: 'Filter_Checkbox_List',
		field_name: 'tags.HENI_Talks_Tags_id.name',
		id: 'themes',
		label: '',
		select_all: true,
		type: 'checkbox',
		filter_checkboxes: themes.map((speaker, index) => {
			return {
				__typename: 'Filter_Checkbox',
				id: index.toString(),
				label: speaker.name,

				default_state: false,
				value: speaker.name,
			};
		}),
	};

	const checkboxes: FilterSectionFilterType = {
		__typename: 'Filter_Section_filter_type',
		collection: FilterCollection.Filter_Checkbox_List,
		id: FilterTypeId.Themes,
		item,
	};

	return {
		__typename: 'Filter_Section',
		id: FilterSectionId.Themes,
		title: 'Themes',
		default_open: true,
		show_content_only: false,
		filter_type: [checkboxes],
	};
};

const getTimePeriods = (
	speakers: TalksGalleryFilterOptions['timePeriods'] = []
): FilterSection => {
	const item: FilterCheckboxList = {
		__typename: 'Filter_Checkbox_List',
		field_name: 'time_period.name',
		id: 'time_periods',
		label: '',
		select_all: true,
		type: 'checkbox',
		filter_checkboxes: speakers.map((speaker, index) => {
			return {
				__typename: 'Filter_Checkbox',
				id: index.toString(),
				label: speaker.name,

				default_state: false,
				value: speaker.name,
			};
		}),
	};

	const checkboxes: FilterSectionFilterType = {
		__typename: 'Filter_Section_filter_type',
		collection: FilterCollection.Filter_Checkbox_List,
		id: FilterTypeId.TimePeriod,
		item,
	};

	return {
		__typename: 'Filter_Section',
		id: FilterSectionId.TimePeriod,
		title: 'Time Periods',
		default_open: true,
		show_content_only: false,
		filter_type: [checkboxes],
	};
};

const getLimitedTimeToggle = (): FilterSection => {
	const item: FilterToggle = {
		__typename: 'Filter_Toggle',
		id: '1',
		label: 'Limited Time',
		field_name: '',
		default_state: false,
	};

	const filterType: FilterSectionFilterType = {
		__typename: 'Filter_Section_filter_type',
		collection: FilterCollection.Filter_Toggle,
		id: FilterTypeId.LimitedTime,
		item,
	};

	return {
		__typename: 'Filter_Section',
		id: FilterSectionId.LimitedTime,
		title: '',
		default_open: true,
		show_content_only: true,
		filter_type: [filterType],
	};
};

const getLimitedTimeCustomAttribute: GetCustomFilterAttribute = ({ value }) => {
	if (!value) return null;

	return {
		displaying_until: {
			_neq: null,
		},
	};
};

export const talksGalleryCustomAttributes: FilterDrawerCustomAttributes = {
	[FilterTypeId.LimitedTime]: getLimitedTimeCustomAttribute,
};

export const getTalksGalleryFilterSections = ({
	filterOptions,
}: {
	filterOptions: TalksGalleryFilterOptions | undefined;
}) => {
	const filterSections: FilterSections = [];

	if (filterOptions?.speakers) {
		filterSections.push(getSpeakers(filterOptions.speakers));
	}

	if (filterOptions?.series) {
		filterSections.push(getSeries(filterOptions.series));
	}

	if (filterOptions?.themes) {
		filterSections.push(getThemes(filterOptions.themes));
	}

	if (filterOptions?.timePeriods) {
		filterSections.push(getTimePeriods(filterOptions.timePeriods));
	}

	filterSections.push(getLimitedTimeToggle());

	return filterSections;
};
